import { useDevice } from '@/hooks/useDevice'
import { SharedProps } from '@/types'
import { Head, router, usePage } from '@inertiajs/react'
import { ActionIcon, Button, Container, Divider, Group, Paper, Stack, TextInput, Title } from '@mantine/core'
import { IconCameraRotate, IconSearch } from '@tabler/icons-react'
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode'
import { useEffect, useRef, useState } from 'react'

export default function Scan() {
  const { current_organization: organization } = usePage<SharedProps>().props
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment')
  const [orderNumber, setOrderNumber] = useState('')
  const qrRef = useRef<Html5Qrcode | null>(null)
  const { isMobile } = useDevice()

  useEffect(() => {
    let isMounted = true
    const html5QrCode = new Html5Qrcode('reader')
    qrRef.current = html5QrCode

    const startScanner = async () => {
      // Small delay to ensure DOM is fully ready
      await new Promise((resolve) => setTimeout(resolve, 100))

      if (!isMounted || !document.getElementById('reader')) return

      try {
        await html5QrCode.start(
          { facingMode },
          {
            fps: 15,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const minEdge = Math.min(viewfinderWidth, viewfinderHeight)
              const size = Math.max(200, Math.floor(minEdge * 0.7))
              return { width: size, height: size }
            },
            aspectRatio: 1.0,
          },
          onScanSuccess,
          onScanFailure
        )

        // CRITICAL: Check if we unmounted while waiting for start()
        if (!isMounted) {
          console.log('Unmounted during start, stopping scanner...')
          await html5QrCode.stop()
          html5QrCode.clear()
        }
      } catch (err) {
        if (isMounted) {
          console.error('Camera start error:', err)
        }
      }
    }

    startScanner()

    return () => {
      isMounted = false
      qrRef.current = null

      try {
        const state = html5QrCode.getState()
        // If we are currently scanning or paused, stop it.
        // If we are INITIALIZING or NOT_STARTED, we can't stop it yet (start() is pending),
        // so we rely on the !isMounted check inside startScanner to verify it later.
        if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
          html5QrCode
            .stop()
            .catch((err) => console.warn('Failed to stop scanner:', err))
            .finally(() => html5QrCode.clear())
        } else {
          // If state is unknown or not started, simpler cleanup
          html5QrCode.clear()
        }
      } catch (err) {
        console.warn('Error during scanner cleanup:', err)
        html5QrCode.clear()
      }
    }
  }, [facingMode])

  const onScanSuccess = async (decodedText: string) => {
    if (!qrRef.current) return

    try {
      if (qrRef.current.getState() === Html5QrcodeScannerState.SCANNING) {
        await qrRef.current.stop()
        qrRef.current.clear()
      }

      if (decodedText.includes('/organizations/')) {
        router.visit(new URL(decodedText).pathname)
        return
      }

      // Legacy format: org:slug:order:id:item:id (backward compat with old printed tickets)
      const legacyMatch = decodedText.match(/org:([^:]+):order:(\d+):item:(\d+)/)
      if (legacyMatch) {
        const [, organizationSlug, orderId, itemId] = legacyMatch
        router.visit(`/organizations/${organizationSlug}/orders/${orderId}/articles/${itemId}`)
        return
      }

      alert('QR Code invalide : ' + decodedText)
      window.location.reload()
    } catch (err) {
      console.warn('Scan processing stop ignored:', err)
    }
  }

  const onScanFailure = () => {
    //Ignore, just continue scanning
  }

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'))
  }

  const handleManualSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!orderNumber) return

    if (!organization) {
      alert("Veuillez d'abord sélectionner une organisation")
      return
    }

    router.visit(`/organizations/${organization.slug}/orders/${orderNumber}`)
  }

  return (
    <>
      <Head title="Scanner QR Code" />

      <Container size="xs" py="xl">
        <Group justify="space-between" align="center" mb="lg">
          <Title order={2} ta="center">
            Scanner un QR Code
          </Title>
          {isMobile && (
            <ActionIcon variant="light" size="lg" radius="md" onClick={toggleCamera} title="Changer de caméra">
              <IconCameraRotate size={20} />
            </ActionIcon>
          )}
        </Group>

        <Stack gap="md">
          <Paper shadow="sm" p="md" withBorder radius="md">
            <div
              id="reader"
              style={{
                width: '100%',
                aspectRatio: '1 / 1', // Match the forced 1.0 aspect ratio from config
                borderRadius: 8,
                overflow: 'hidden',
              }}
            />
          </Paper>

          <Divider label="OU" labelPosition="center" />

          <Paper shadow="sm" p="md" withBorder radius="md">
            <form onSubmit={handleManualSearch}>
              <Stack gap="sm">
                <Title order={4}>Chercher par numéro</Title>
                <Group align="flex-end" grow>
                  <TextInput
                    placeholder="Numéro de commande (ex: 123)"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.currentTarget.value)}
                    leftSection={<IconSearch size={16} />}
                  />
                  <Button type="submit" style={{ flexGrow: 0 }} disabled={!orderNumber}>
                    Chercher
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Stack>
      </Container>
    </>
  )
}
